import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAllgemeineDaten } from "../../context/AllgemeineDatenContext";
import styles from "./AllgemeineDaten.module.css";

const AllgemeineDaten = () => {
  const navigate = useNavigate();
  const { data, setData } = useAllgemeineDaten();
  const [errors, setErrors] = useState({});
  const [showDropdownSonst, setShowDropdownSonst] = useState(
    data.diabetestyp === "sonstiges"
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  // Auto-calc BMI (only when valid)
  useEffect(() => {
    const height = parseFloat(data.groeße);
    const weight = parseFloat(data.gewicht);

    const heightValid = height >= 1 && height <= 300;
    const weightValid = weight >= 1 && weight <= 500;

    if (!heightValid || !weightValid) {
      setData((prev) => ({ ...prev, bmi: "" }));
      return;
    }

    if (height > 0 && weight > 0) {
      const bmi = weight / Math.pow(height / 100, 2);
      setData((prev) => ({ ...prev, bmi: Number(bmi.toFixed(2)) }));
    }
  }, [data.groeße, data.gewicht, setData]);

  // Handle change + validation
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // if (data.shopListePatient === true && 
    //     (name === "name" || name === "vorname" || name === "geburtsdatum")) {
    //   return;
    // }

    setData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    let err = null;
    if (name === "groeße") {
      const v = parseFloat(value);
      if (value && (v < 1 || v > 300)) {
        err = "Größe muss gültig sein";
      }
    }
    if (name === "gewicht") {
      if (value === "") {
        err = null;
      } else {
        const normalized = value.replace(",", ".");
        const v = parseFloat(normalized);
        const decimalPart = normalized.split(".")[1] || "";
        if (
          isNaN(v) ||
          v < 1 ||
          v > 500 ||
          decimalPart.length > 3 ||
          !/^\d+(\.\d{0,3})?$/.test(normalized)
        ) {
          err = "Gewicht muss gültig sein und max 3 Dezimalstellen";
        }
      }
    }

    setErrors((prev) => ({ ...prev, [name]: err }));

    if (name === "diabetestyp") {
      setShowDropdownSonst(value === "sonstiges");
    }
  };

  // Submit validation
  const validateForm = () => {
    const newErrors = {};

    if (!data.name) newErrors.name = "Bitte geben Sie einen Namen ein";
    if (!data.vorname)
      newErrors.vorname = "Bitte geben Sie einen Vornamen ein";

    if (!data.groeße) {
      newErrors.groeße = "Bitte geben Sie eine Größe ein";
    } else if (data.groeße < 1 || data.groeße > 300) {
      newErrors.groeße = "Größe muss gültig sein";
    }

    if (!data.gewicht) {
      newErrors.gewicht = "Bitte geben Sie ein Gewicht ein";
    } else {
      const normalized = data.gewicht.replace(",", ".");
      const v = parseFloat(normalized);
      const decimalPart = normalized.split(".")[1] || "";

      if (
        isNaN(v) ||
        v < 1 ||
        v > 500 ||
        decimalPart.length > 3 ||
        !/^\d+(\.\d{0,3})?$/.test(normalized)
      ) {
        newErrors.gewicht = "Gewicht muss gültig sein und max 3 Dezimalstellen";
      }
    }

    if (!data.geburtsdatum)
      newErrors.geburtsdatum = "Bitte geben Sie ein Geburtsdatum ein";
    if (!data.geschlecht)
      newErrors.geschlecht = "Bitte wählen Sie ein Geschlecht";
    if (!data.mrsa) newErrors.mrsa = "Bitte wählen Sie eine Option";
    if (!data.diabSchulung)
      newErrors.diabSchulung = "Bitte wählen Sie eine Option";
    if (!data.diabetestyp)
      newErrors.diabetestyp = "Bitte wählen Sie eine Option";
    if (!data.dauerDiab)
      newErrors.dauerDiab = "Bitte geben Sie die Dauer ein";
    if (!data.datum) newErrors.datum = "Bitte wählen Sie ein Datum";
    if (!data.fussAuswahl) newErrors.fussAuswahl = "Bitte wählen Sie eine Option";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle submit
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    navigate("/klinischerBefund");
  };

  return (
    <div className={styles["allgemeine-daten-page"]}>
      <h1>Allgemeine Daten</h1>

      <form className={styles["form-horizontal"]} onSubmit={handleSubmit}>
        {/* ROW 1 */}
        <div className={styles.row}>
          <div className={styles["col-sm-4"]}>
            <label className={styles.label}>Name</label>
            <input
              type="text"
              name="name"
              className={styles.input}
              value={data.name || ""}
              onChange={handleChange}
              // readOnly={data.shopListePatient}
            />
            {errors.name && (
              <p className={styles["error-text"]}>{errors.name}</p>
            )}
          </div>

          <div className={styles["col-sm-4"]}>
            <label className={styles.label}>Vorname</label>
            <input
              type="text"
              name="vorname"
              className={styles.input}
              value={data.vorname || ""}
              onChange={handleChange}
              // readOnly={data.shopListePatient}
            />
            {errors.vorname && (
              <p className={styles["error-text"]}>{errors.vorname}</p>
            )}
          </div>

          <div className={styles["col-sm-4"]}>
            <label className={styles.label}>Patienten-ID</label>
            <input
              type="text"
              name="barcode"
              className={styles.input}
              value={data.shopPatientId || data.barcode || ""}
              readOnly
            />
          </div>
        </div>

        {/* ROW 2 HEIGHT / WEIGHT / BMI */}
        <div className={styles.row}>
          <div className={styles["col-sm-4"]}>
            <label className={styles.label}>Größe (cm)</label>
            <input
              type="number"
              name="groeße"
              className={styles.input}
              min="1"
              max="300"
              value={data.groeße || ""}
              onChange={handleChange}
            />
            {errors.groeße && (
              <p className={styles["error-text"]}>{errors.groeße}</p>
            )}
          </div>

          <div className={styles["col-sm-4"]}>
            <label className={styles.label}>Gewicht (kg)</label>
            <input
              type="text"
              name="gewicht"
              className={styles.input}
              inputMode="decimal"
              pattern="[0-9]*[.,]?[0-9]{0,3}"
              value={data.gewicht || ""}
              onChange={handleChange}
              onBlur={(e) => {
                const val = e.target.value.trim().replace(",", ".");
                if (val && !isNaN(parseFloat(val))) {
                  setData((prev) => ({ ...prev, gewicht: val }));
                }
              }}
            />
            {errors.gewicht && (
              <p className={styles["error-text"]}>{errors.gewicht}</p>
            )}
          </div>

          <div className={styles["col-sm-4"]}>
            <label className={styles.label}>BMI</label>
            <input
              type="text"
              className={styles.input}
              value={data.bmi ?? ""}
              readOnly
            />
          </div>
        </div>

        {/* ROW 3 */}
        <div className={styles.row}>
          <div className={styles["col-sm-4"]}>
            <label className={styles.label}>Geburtsdatum</label>
            <input
              type="date"
              name="geburtsdatum"
              className={styles.input}
              value={data.geburtsdatum || ""}
              onChange={handleChange}
              // readOnly={data.shopListePatient}
            />
            {errors.geburtsdatum && (
              <p className={styles["error-text"]}>{errors.geburtsdatum}</p>
            )}
          </div>

          <div className={styles["col-sm-4"]}>
            <label className={styles.label}>Geschlecht</label>
            <div className={styles["radio-group"]}>
              {["männlich", "weiblich", "divers", "Keine Angabe"].map((v) => (
                <label key={v} className={styles["radio-inline"]}>
                  <input
                    type="radio"
                    name="geschlecht"
                    value={v}
                    checked={data.geschlecht === v}
                    onChange={handleChange}
                  />{" "}
                  {v}
                </label>
              ))}
            </div>
            {errors.geschlecht && (
              <p className={styles["error-text"]}>{errors.geschlecht}</p>
            )}
          </div>

          <div className={styles["col-sm-4"]}>
            <label className={styles.label}>MRSA</label>
            <div className={styles["radio-group"]}>
              {["ja", "nein", "unbekannt"].map((v) => (
                <label key={v} className={styles["radio-inline"]}>
                  <input
                    type="radio"
                    name="mrsa"
                    value={v}
                    checked={data.mrsa === v}
                    onChange={handleChange}
                  />{" "}
                  {v}
                </label>
              ))}
            </div>
            {errors.mrsa && (
              <p className={styles["error-text"]}>{errors.mrsa}</p>
            )}
          </div>
        </div>

        {/* ROW 4 */}
        <div className={styles.row}>
          <div className={styles["col-sm-4"]}>
            <label className={styles.label}>Teilnahme an Diab.-Schulung</label>
            <div className={styles["radio-group"]}>
              {["ja", "nein"].map((v) => (
                <label key={v} className={styles["radio-inline"]}>
                  <input
                    type="radio"
                    name="diabSchulung"
                    value={v}
                    checked={data.diabSchulung === v}
                    onChange={handleChange}
                  />{" "}
                  {v}
                </label>
              ))}
            </div>
            {errors.diabSchulung && (
              <p className={styles["error-text"]}>{errors.diabSchulung}</p>
            )}
          </div>

          <div className={styles["col-sm-4"]}>
            <label className={styles.label}>Diab. mell.</label>
            <div className={styles["radio-group"]}>
              {["typ 1", "typ 2", "sonstiges"].map((v) => (
                <label key={v} className={styles["radio-inline"]}>
                  <input
                    type="radio"
                    name="diabetestyp"
                    value={v}
                    checked={data.diabetestyp === v}
                    onChange={handleChange}
                  />{" "}
                  {v}
                </label>
              ))}
            </div>
            {errors.diabetestyp && (
              <p className={styles["error-text"]}>{errors.diabetestyp}</p>
            )}
          </div>

          <div className={styles["col-sm-4"]}>
            <label className={styles.label}>
              Dauer des Diab. mell. (Jahre)
            </label>
            <input
              type="number"
              name="dauerDiab"
              className={styles.input}
              value={data.dauerDiab || ""}
              onChange={handleChange}
            />
            {errors.dauerDiab && (
              <p className={styles["error-text"]}>{errors.dauerDiab}</p>
            )}
          </div>
        </div>

        {/* ROW 5 - Datum (original layout) + Fuß Auswahl */}
        <div className={styles.row}>
          <div className={styles["single-field-col"]}>
            <label className={styles.label}>Datum</label>
            <input
              type="date"
              name="datum"
              className={styles.input}
              value={data.datum || ""}
              onChange={handleChange}
            />
            {errors.datum && (
              <p className={styles["error-text"]}>{errors.datum}</p>
            )}
          </div>

          <div className={styles["fuss-container"]}>
            <label className={`${styles.label} ${styles["fuss-label"]}`}>
              Bitte geben Sie an, für welchen Fuß / welche Füße die Daten erhoben werden sollen
            </label>
            <div className={`${styles["radio-group"]} ${styles["fuss-radio-group"]}`}>
              {["beide", "links", "rechts"].map((v) => (
                <label key={v} className={styles["radio-inline"]}>
                  <input
                    type="radio"
                    name="fussAuswahl"
                    value={v}
                    checked={data.fussAuswahl === v}
                    onChange={handleChange}
                  />{" "}
                  {v === "beide" ? "Beide" : v === "links" ? "Links" : "Rechts"}
                </label>
              ))}
            </div>
              {errors.fussAuswahl && (
                <p className={`${styles["error-text"]} ${styles["fuss-error"]}`}>
                  {errors.fussAuswahl}
                </p>
              )}
          </div>
        </div>

        {/* ROW 6 — Actions */}
        <div className={`${styles.row} ${styles.actions}`}>
          <button
            type="button"
            className={styles["back-btn"]}
            onClick={() => navigate("/patientenliste")}
          >
            Zurück
          </button>

          <input
            type="submit"
            value="Weiter"
            className={styles["btn-primary"]}
          />
        </div>
      </form>
    </div>
  );
};

export default AllgemeineDaten;
